import type { ComponentType } from "react";
import { Navigate, createBrowserRouter } from "react-router";
import { AppLayout, DashboardShell } from "./components/AppLayout";
import { HomePage } from "./pages/HomePage";
import { InvitePage } from "./pages/InvitePage";
import { LoginCallbackPage } from "./pages/LoginCallbackPage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { RouteErrorPage } from "./pages/RouteErrorPage";

/**
 * Loads a page module only when its route is first visited, so the initial download covers
 * what most visits need (sign-in, home, payments) and settings/developers come on demand.
 */
function page<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M) {
  return async () => ({ Component: (await load())[name] as ComponentType });
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: "/login/callback", element: <LoginCallbackPage />, errorElement: <RouteErrorPage /> },
  { path: "/invite/:token", element: <InvitePage />, errorElement: <RouteErrorPage /> },
  // Links in the payout change email; outside the app shell, like sign-in.
  {
    path: "/payout-change/confirm",
    lazy: page(() => import("./pages/PayoutChangePage"), "ConfirmPayoutChangePage"),
    errorElement: <RouteErrorPage />,
  },
  {
    path: "/payout-change/cancel",
    lazy: page(() => import("./pages/PayoutChangePage"), "CancelPayoutChangePage"),
    errorElement: <RouteErrorPage />,
  },
  {
    element: <AppLayout />,
    // Any page (or lazy chunk) that fails ends here instead of the router's developer screen.
    errorElement: <RouteErrorPage />,
    children: [
      {
        // The business dashboard: sidebar, profile switcher, search and test mode.
        element: <DashboardShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: "dashboard", element: <HomePage /> },
          { path: "payments", element: <PaymentsPage /> },
          { path: "payment-links", element: <Navigate to="/payments?source=payment_link" replace /> },
          {
            path: "transactions",
            lazy: page(() => import("./pages/TransactionsPage"), "TransactionsPage"),
          },
          {
            path: "transactions/:id",
            lazy: page(() => import("./pages/TransactionDetailPage"), "TransactionDetailPage"),
          },
          {
            path: "payments/new",
            lazy: page(() => import("./pages/CreatePaymentLinkPage"), "CreatePaymentLinkPage"),
          },
          {
            path: "payments/:id",
            lazy: page(() => import("./pages/PaymentDetailPage"), "PaymentDetailPage"),
          },
          // Moved into Settings; kept so old links (and older emails) still land.
          { path: "payouts", element: <Navigate to="/settings/payouts" replace /> },
          // Moved into Settings; kept so old links still land.
          { path: "checkout", element: <Navigate to="/settings/payment-defaults#accepted-tokens" replace /> },
          {
            path: "payment-page",
            element: <Navigate to="/settings/payment-defaults#accepted-tokens" replace />,
          },
          {
            path: "settings",
            lazy: page(() => import("./pages/settings/SettingsLayout"), "SettingsLayout"),
            children: [
              { index: true, element: <Navigate to="business" replace /> },
              {
                path: "business",
                lazy: page(() => import("./pages/settings/BusinessSettingsPage"), "BusinessSettingsPage"),
              },
              { path: "payouts", lazy: page(() => import("./pages/payouts/PayoutsPage"), "PayoutsPage") },
              {
                path: "payment-defaults",
                lazy: page(() => import("./pages/settings/PaymentDefaultsPage"), "PaymentDefaultsPage"),
              },
              // Now a card on Payment defaults; kept so old links still land.
              {
                path: "accepted-tokens",
                element: <Navigate to="/settings/payment-defaults#accepted-tokens" replace />,
              },
              {
                path: "checkout",
                element: <Navigate to="/settings/payment-defaults#accepted-tokens" replace />,
              },
              {
                path: "team",
                lazy: page(() => import("./pages/settings/TeamSettingsPage"), "TeamSettingsPage"),
              },
              {
                path: "activity",
                lazy: page(() => import("./pages/settings/ActivitySettingsPage"), "ActivitySettingsPage"),
              },
              // Moved out of profile settings; kept so old links still land.
              { path: "account", element: <Navigate to="/account" replace /> },
              { path: "settlement", element: <Navigate to="/settings/payouts" replace /> },
              {
                path: "hosted-page",
                element: <Navigate to="/settings/payment-defaults#accepted-tokens" replace />,
              },
            ],
          },
          {
            path: "account",
            lazy: page(() => import("./pages/account/AccountLayout"), "AccountLayout"),
            children: [
              { index: true, element: <Navigate to="details" replace /> },
              { path: "details", lazy: page(() => import("./pages/account/DetailsPage"), "DetailsPage") },
              { path: "security", lazy: page(() => import("./pages/account/SecurityPage"), "SecurityPage") },
              { path: "security/pin", lazy: page(() => import("./pages/account/PinPage"), "PinPage") },
              { path: "profiles", lazy: page(() => import("./pages/account/ProfilesPage"), "ProfilesPage") },
              // Moved; kept so old links still land.
              { path: "general", element: <Navigate to="/account/details" replace /> },
              { path: "wallet", element: <Navigate to="/wallet" replace /> },
            ],
          },
          { path: "wallet", lazy: page(() => import("./pages/wallet/WalletPage"), "WalletPage") },
          { path: "wallet/receive", lazy: page(() => import("./pages/wallet/ReceivePage"), "ReceivePage") },
          { path: "wallet/send", lazy: page(() => import("./pages/wallet/SendPage"), "SendPage") },
          {
            path: "developers",
            lazy: page(() => import("./pages/developers/DevelopersLayout"), "DevelopersLayout"),
            children: [
              { index: true, element: <Navigate to="integration" replace /> },
              {
                path: "integration",
                lazy: page(() => import("./pages/developers/IntegrationPage"), "IntegrationPage"),
              },
              { path: "api-keys", lazy: page(() => import("./pages/developers/ApiKeysPage"), "ApiKeysPage") },
              {
                path: "webhooks",
                lazy: page(() => import("./pages/developers/WebhooksPage"), "WebhooksPage"),
              },
              {
                path: "webhooks/:id",
                lazy: page(() => import("./pages/developers/WebhookEndpointPage"), "WebhookEndpointPage"),
              },
            ],
          },
        ],
      },
      // Full screen, without the dashboard chrome.
      {
        path: "profiles/new",
        lazy: page(() => import("./pages/new-profile/NewProfilePage"), "NewProfilePage"),
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
